import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Spinner,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tree,
  TreeItem,
  TreeItemLabel,
  Typography,
} from '@locus-taxy/disco-ui';
import { useTree } from '@headless-tree/react';
import {
  syncDataLoaderFeature,
  selectionFeature,
  hotkeysCoreFeature,
} from '@headless-tree/core';
import { PageHeader } from '@locus-taxy/encore-ui-toolkit';
import { Maximize2, Minimize2 } from 'lucide-react';
import { DatabricksDashboard } from '@databricks/aibi-client';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useLocation, useMatch } from 'react-router-dom';
import {
  useEntitySearch,
  type EntitySearchRequest,
} from '../api/endpoints/useEntitySearch';
import {
  useAnalyticsDashboardToken,
  type AnalyticsDashboardTokenData,
} from '../api/endpoints/useAnalyticsDashboardToken';
import type { DashboardMetadata } from '../types/dashboardMetadata';
import { useClientId } from '../hooks/useClientId';
import { buildClientPrefixedPath } from '../utils/url';
import { RoutePath } from '../types/categories';
import { useDashboardNavigate } from '../hooks/useDashboardNavigate';
import { getTestId } from '@locus-taxy/encore-utils';
import { useRouteContext } from '../hooks/useRouteContext';
import { TestIdAction } from '../utils/testIdUtils';

const DASHBOARD_PAGE_SIZE = 100;

interface DashboardAccessParameters {
  token: string | null;
  tokenType?: string | null;
}

// Fullscreen helpers to support cross-browser behavior
type VendorDocument = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
};

type VendorHTMLElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

function getFullscreenElement(): Element | null {
  const vendorDoc = document as VendorDocument;
  const candidates = [
    document.fullscreenElement,
    vendorDoc.webkitFullscreenElement,
    vendorDoc.mozFullScreenElement,
    vendorDoc.msFullscreenElement,
  ];
  return candidates.find(Boolean) ?? null;
}

function requestElementFullscreen(el: HTMLElement) {
  const vendorEl = el as VendorHTMLElement;
  (
    vendorEl.requestFullscreen ||
    vendorEl.webkitRequestFullscreen ||
    vendorEl.mozRequestFullScreen ||
    vendorEl.msRequestFullscreen
  )?.call(vendorEl);
}

/**
 * Returns the first value that is a string, or null if none are.
 */
function firstStringValue(...values: unknown[]): string | null {
  const match = values.find(value => typeof value === 'string');
  return match ?? null;
}

function exitDocumentFullscreen() {
  const vendorDoc = document as VendorDocument;
  (
    document.exitFullscreen ||
    vendorDoc.webkitExitFullscreen ||
    vendorDoc.mozCancelFullScreen ||
    vendorDoc.msExitFullscreen
  )?.call(document);
}

interface DashboardPickerItem {
  id: string;
  label: string;
}

interface DashboardPickerTreeProps {
  readonly items: DashboardPickerItem[];
  readonly activePath: string[];
  readonly onItemClick: (path: string[]) => void;
}

function DashboardPickerTree({
  items,
  activePath,
  onItemClick,
}: DashboardPickerTreeProps) {
  const itemMap = useMemo(() => {
    const m: Record<string, DashboardPickerItem & { childrenIds: string[] }> = {
      __root: { id: '__root', label: '', childrenIds: items.map(i => i.id) },
    };
    for (const it of items) {
      m[it.id] = { ...it, childrenIds: [] };
    }
    return m;
  }, [items]);

  const tree = useTree<DashboardPickerItem & { childrenIds: string[] }>({
    rootItemId: '__root',
    getItemName: item => item.getItemData().label,
    isItemFolder: () => false,
    dataLoader: {
      getItem: id => itemMap[id],
      getChildren: id => itemMap[id]?.childrenIds ?? [],
    },
    features: [syncDataLoaderFeature, selectionFeature, hotkeysCoreFeature],
  });

  useEffect(() => {
    const activeId = activePath[activePath.length - 1];
    if (activeId) {
      tree.getItemInstance(activeId)?.select();
    }
  }, [activePath, tree]);

  return (
    <Tree tree={tree}>
      {tree.getItems().map(item => (
        <TreeItem
          key={item.getId()}
          item={item}
          onClick={() => onItemClick([item.getId()])}
        >
          <TreeItemLabel>{item.getItemName()}</TreeItemLabel>
        </TreeItem>
      ))}
    </Tree>
  );
}

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const navigate = useDashboardNavigate();
  const location = useLocation();
  const { entity, page } = useRouteContext();
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(
    null
  );
  const [hasEmbedError, setHasEmbedError] = useState(false);
  const dashboardContainerRef = useRef<HTMLDivElement | null>(null);
  const dashboardInstanceRef = useRef<DatabricksDashboard | null>(null);
  const previousDashboardIdRef = useRef<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const searchRequest = useMemo<EntitySearchRequest>(
    () => ({
      request: {
        sort: [{ field: 'entityData.description', direction: 'ASC' }],
        pagination: { page: 1, size: DASHBOARD_PAGE_SIZE },
      },
    }),
    []
  );

  const { data, isLoading, isError } = useEntitySearch<DashboardMetadata>(
    clientId,
    'dashboardMetadata',
    searchRequest,
    { enabled: Boolean(clientId) }
  );

  const dashboards = useMemo<DashboardMetadata[]>(() => {
    const records = data?.data?.content ?? [];
    return records
      .map(record => record.entityData)
      .filter((entity): entity is DashboardMetadata => Boolean(entity));
  }, [data?.data?.content]);

  const dashboardMatch = useMatch(
    '/clientId/:clientId/analytics/dashboards/:dashboardId'
  );
  const urlDashboardId =
    (dashboardMatch?.params?.dashboardId as string) || null;

  useEffect(() => {
    if (!dashboards.length) {
      setSelectedDashboardId(null);
      return;
    }

    if (urlDashboardId) {
      if (dashboards.some(d => d.id === urlDashboardId)) {
        setSelectedDashboardId(urlDashboardId);
        return;
      }
      const firstId = dashboards[0]?.id;
      if (firstId) {
        navigate(
          buildClientPrefixedPath(
            `${RoutePath.ANALYTICS}/dashboards/${firstId}`,
            clientId
          ),
          { replace: true }
        );
      }
      return;
    }

    const firstId = dashboards[0]?.id ?? null;
    if (firstId) {
      const analyticsHomePath = buildClientPrefixedPath(
        RoutePath.ANALYTICS,
        clientId
      );
      const pathname = location.pathname;
      if (
        pathname === analyticsHomePath ||
        pathname === `${analyticsHomePath}/`
      ) {
        navigate(
          buildClientPrefixedPath(
            `${RoutePath.ANALYTICS}/dashboards/${firstId}`,
            clientId
          ),
          { replace: true }
        );
        setSelectedDashboardId(firstId);
        return;
      }
    }

    setSelectedDashboardId(prev => {
      if (prev && dashboards.some(d => d.id === prev)) {
        return prev;
      }
      return dashboards[0]?.id ?? null;
    });
  }, [dashboards, urlDashboardId, clientId, navigate, location.pathname]);

  const selectedDashboard = useMemo(
    () => dashboards.find(d => d.id === selectedDashboardId) ?? null,
    [dashboards, selectedDashboardId]
  );

  const activeDashboardContext = useMemo(
    () =>
      selectedDashboard
        ? {
            dashboardId: selectedDashboard.dashboardId,
            workspaceId: selectedDashboard.workspaceId,
            instanceUrl: selectedDashboard.instanceUrl,
          }
        : null,
    [selectedDashboard]
  );

  const {
    data: tokenResponse,
    isLoading: isTokenLoading,
    isError: isTokenError,
    refetch: refetchToken,
  } = useAnalyticsDashboardToken(clientId, selectedDashboard?.id, {
    enabled: Boolean(selectedDashboard?.id),
  });

  const resolveTokenDetails = useCallback(
    (
      payload: AnalyticsDashboardTokenData | null | undefined
    ): DashboardAccessParameters => {
      if (!payload) {
        return { token: null, tokenType: null };
      }

      const tokenCandidate = firstStringValue(
        payload.access_token,
        payload.accessToken,
        payload.token
      );

      const tokenTypeCandidate = firstStringValue(
        payload.token_type,
        payload.tokenType
      );

      return {
        token: tokenCandidate,
        tokenType: tokenTypeCandidate,
      };
    },
    []
  );

  const tokenDetails = useMemo(
    () => resolveTokenDetails(tokenResponse?.data),
    [resolveTokenDetails, tokenResponse?.data]
  );

  const getNewToken = useCallback(async () => {
    const refreshed = await refetchToken();
    const nextPayload = refreshed.data?.data;
    const nextDetails = resolveTokenDetails(nextPayload);
    if (!nextDetails.token) {
      throw new Error('Failed to refresh analytics dashboard token');
    }
    return nextDetails.token;
  }, [refetchToken, resolveTokenDetails]);

  const destroyDashboardInstance = useCallback(() => {
    if (dashboardInstanceRef.current) {
      dashboardInstanceRef.current.destroy();
      dashboardInstanceRef.current = null;
    }
    if (dashboardContainerRef.current) {
      dashboardContainerRef.current.innerHTML = '';
    }
    previousDashboardIdRef.current = null;
  }, []);

  const navigationItems = useMemo<DashboardPickerItem[]>(
    () =>
      dashboards.map(dashboard => {
        const labelText =
          dashboard.description ??
          t('dashboard.analytics.unnamedDashboard', 'Untitled dashboard');
        return { id: dashboard.id, label: labelText };
      }),
    [dashboards, t]
  );

  const activeNavigationPath = useMemo(
    () => (selectedDashboardId ? [selectedDashboardId] : []),
    [selectedDashboardId]
  );

  const handleNavigationClick = useCallback(
    (path: string[]) => {
      const lastId = path[path.length - 1] ?? null;
      setHasEmbedError(false);
      if (lastId) {
        setSelectedDashboardId(lastId);
        navigate(
          buildClientPrefixedPath(
            `${RoutePath.ANALYTICS}/dashboards/${lastId}`,
            clientId
          )
        );
      }
    },
    [clientId, navigate]
  );

  useEffect(() => {
    const container = dashboardContainerRef.current;

    if (!container) {
      return;
    }

    if (!activeDashboardContext || !tokenDetails.token) {
      destroyDashboardInstance();
      if (hasEmbedError) {
        setHasEmbedError(false);
      }
      return;
    }

    if (hasEmbedError) {
      return;
    }

    const shouldReinitialize =
      !dashboardInstanceRef.current ||
      previousDashboardIdRef.current !== activeDashboardContext.dashboardId;

    if (!shouldReinitialize) {
      return;
    }

    destroyDashboardInstance();

    try {
      const dashboard = new DatabricksDashboard({
        workspaceId: activeDashboardContext.workspaceId,
        instanceUrl: activeDashboardContext.instanceUrl,
        container,
        dashboardId: activeDashboardContext.dashboardId,
        token: tokenDetails.token,
        getNewToken,
        colorScheme: 'light',
      });

      dashboard.initialize();
      dashboardInstanceRef.current = dashboard;
      previousDashboardIdRef.current = activeDashboardContext.dashboardId;
    } catch {
      destroyDashboardInstance();
      setHasEmbedError(true);
    }
  }, [
    activeDashboardContext,
    destroyDashboardInstance,
    hasEmbedError,
    getNewToken,
    tokenDetails.token,
  ]);

  useEffect(
    () => () => {
      destroyDashboardInstance();
    },
    [destroyDashboardInstance]
  );

  const headerTitle = t('dashboard.analytics.title', 'Analytics');

  const handleBackClick = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(buildClientPrefixedPath('/', clientId), { replace: true });
  }, [clientId, navigate]);

  useEffect(() => {
    const update = () => {
      const current = getFullscreenElement();
      setIsFullscreen(Boolean(current));
    };
    document.addEventListener('fullscreenchange', update);
    document.addEventListener(
      'webkitfullscreenchange',
      update as EventListener
    );
    document.addEventListener('MSFullscreenChange', update as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener(
        'webkitfullscreenchange',
        update as EventListener
      );
      document.removeEventListener(
        'MSFullscreenChange',
        update as EventListener
      );
    };
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    const container = dashboardContainerRef.current;
    if (!container) {
      return;
    }
    const current = getFullscreenElement();
    if (current) {
      exitDocumentFullscreen();
    } else {
      requestElementFullscreen(container);
    }
  }, []);

  const enterFsLabel = t(
    'dashboard.analytics.enterFullscreen',
    'Enter fullscreen'
  );
  const exitFsLabel = t(
    'dashboard.analytics.exitFullscreen',
    'Exit fullscreen'
  );

  const actionStack = useMemo(
    () => [
      {
        id: 'toggle-fullscreen',
        icon: (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                {isFullscreen ? (
                  <Minimize2 size={20} />
                ) : (
                  <Maximize2 size={20} />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isFullscreen ? exitFsLabel : enterFsLabel}
            </TooltipContent>
          </Tooltip>
        ),
        ariaLabel: isFullscreen ? exitFsLabel : enterFsLabel,
        onClick: handleToggleFullscreen,
        disabled: !selectedDashboard || isTokenLoading || hasEmbedError,
      },
    ],
    [
      hasEmbedError,
      enterFsLabel,
      exitFsLabel,
      handleToggleFullscreen,
      isFullscreen,
      isTokenLoading,
      selectedDashboard,
    ]
  );

  const navigationContent = useMemo(() => {
    if (isLoading) {
      return (
        <Stack align="center" justify="center" className="h-full p-4">
          <Spinner size="md" />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Typography variant="body2" color="destructive" className="p-4">
          {t(
            'dashboard.analytics.errorLoading',
            'Unable to load dashboards right now.'
          )}
        </Typography>
      );
    }

    if (!dashboards.length) {
      return (
        <Typography variant="body2" color="muted" className="p-4">
          {t(
            'dashboard.analytics.noDashboards',
            'No dashboards are available yet.'
          )}
        </Typography>
      );
    }

    return (
      <DashboardPickerTree
        items={navigationItems}
        activePath={activeNavigationPath}
        onItemClick={handleNavigationClick}
      />
    );
  }, [
    activeNavigationPath,
    dashboards.length,
    handleNavigationClick,
    isError,
    isLoading,
    navigationItems,
    t,
  ]);

  const renderDashboardViewer = () => {
    if (!selectedDashboard) {
      return (
        <Stack
          align="center"
          justify="center"
          className="flex-1 p-6 text-center"
        >
          <Typography variant="body1" color="muted">
            {t(
              'dashboard.analytics.selectDashboardPrompt',
              'Select a dashboard from the list to get started.'
            )}
          </Typography>
        </Stack>
      );
    }

    const dashboardLabel =
      selectedDashboard.description ?? selectedDashboard.id;
    const retryLabel = t('dashboard.analytics.retry', 'Retry');

    if (isTokenLoading) {
      return (
        <Stack
          align="center"
          justify="center"
          gap={2}
          className="flex-1 p-6 text-center"
        >
          <Spinner size="md" />
          <Typography variant="body2" color="muted">
            {t('dashboard.analytics.loadingDashboard', 'Loading dashboard...')}
          </Typography>
        </Stack>
      );
    }

    if (isTokenError) {
      return (
        <Stack
          align="center"
          justify="center"
          gap={2}
          className="flex-1 p-6 text-center"
        >
          <Typography variant="body2" color="destructive">
            {t(
              'dashboard.analytics.tokenError',
              "We couldn't authorize this dashboard. Please try again."
            )}
          </Typography>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchToken()}
            data-testid={getTestId({
              action: TestIdAction.RETRY,
              object: 'button',
              entity: dashboardLabel,
              page: 'analytics',
            })}
          >
            {retryLabel}
          </Button>
        </Stack>
      );
    }

    if (hasEmbedError) {
      return (
        <Stack
          align="center"
          justify="center"
          gap={2}
          className="flex-1 p-6 text-center"
        >
          <Typography variant="body2" color="destructive">
            {t(
              'dashboard.analytics.embedError',
              "We couldn't render the dashboard. Please try again later."
            )}
          </Typography>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setHasEmbedError(false);
              destroyDashboardInstance();
              refetchToken();
            }}
            data-testid={getTestId({
              action: TestIdAction.RETRY,
              object: 'button',
              entity: dashboardLabel,
              page: 'analytics',
            })}
          >
            {retryLabel}
          </Button>
        </Stack>
      );
    }

    if (!tokenDetails.token) {
      return (
        <Stack
          align="center"
          justify="center"
          className="flex-1 p-6 text-center"
        >
          <Typography variant="body2" color="muted">
            {t(
              'dashboard.analytics.tokenUnavailable',
              'Dashboard is temporarily unavailable. Retry in a moment.'
            )}
          </Typography>
        </Stack>
      );
    }

    return (
      <Box
        key={selectedDashboard.dashboardId}
        ref={dashboardContainerRef}
        data-testid="analytics-dashboard-container"
        className="h-full w-full flex-grow"
      />
    );
  };

  return (
    <Stack direction="column" className="my-0 h-full overflow-hidden">
      <PageHeader
        title={headerTitle}
        onBackClick={handleBackClick}
        actionStack={actionStack}
        routeContext={{
          entity: entity ?? 'analytics',
          page: page ?? 'dashboard',
        }}
      />
      <Box
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'analytics-container',
          entity: selectedDashboard?.description ?? selectedDashboard?.id,
          page: 'analytics',
        })}
        className="flex flex-1 flex-col"
      >
        <Stack
          direction="row"
          gap={2}
          className="my-0 flex-grow flex-col md:flex-row"
        >
          <Box className="w-full rounded-lg border bg-card md:w-[280px]">
            <Stack direction="column" gap={2} className="p-4">
              <Box className="min-h-0 flex-grow overflow-auto">
                {navigationContent}
              </Box>
            </Stack>
          </Box>
          <Box className="flex flex-1 flex-col rounded-lg border bg-card">
            {renderDashboardViewer()}
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
}
